import { Component, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { Guid } from 'guid-typescript';
import * as _ from 'lodash';
import {
  AgpSharedAggregatorComponentRegistryService
} from '@liberty-financial/agp-shared';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  of,
  Subscription
} from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  take
} from 'rxjs/operators';

import { Failure } from '@core/failure';
import { FeatureFlagService } from '@core/services/feature-flag.service';
import { UserList } from '@core/services/user';
import { UserService } from '@core/services/user.service';
import { FeatureFlag } from '@env/environment.interface';
import {
  ContactSummary,
  EntityType
} from '@features/contacts/models/contactsummary';
import {
  ContactSearchFilterOptions,
  ContactService
} from '@features/contacts/services/contact.service';
import {
  TagFilterTargets,
  TagFilterScope,
  TagService
} from '@features/opportunities/services/tag.service';
import { ModalSetting } from '@shared/components/modal/modal-setting';
import { SortOption } from '@shared/models/sort-option';

import { CompanyNewComponent } from '../../company/details/new/company-new.component';
import { IndividualNewComponent } from '../../individual/details/new/individual-new.component';
import { TrustNewComponent } from '../../trust/details/new/trust-new.component';
import { ContactReassignComponent } from '../reassign/reassign.component';

type Load = {
  contacts: ContactSummary[] | Failure<any>;
  users: UserList;
};

@Component({
  selector: 'spark-contacts-list',
  templateUrl: './contact-list.component.html',
  styleUrls: ['./contact-list.component.scss']
})
export class ContactListComponent implements OnDestroy, OnInit {
  // Empty UserList for template fallback
  public emptyUserList = new UserList([]);
  
  // Loading state for searches
  public isSearching = false;
  
  defaultFilterOptions: ContactSearchFilterOptions = {
    search: '',
    ownedBy: '',
    tagId: ''
  };
  hasFiltersApplied = false;

  private subscriptions: Subscription[] = [];
  private refresh$ = new BehaviorSubject<null>(null);
  private search$ = new BehaviorSubject<ContactSearchFilterOptions>(
    this.defaultFilterOptions
  );
  public contacts$: Observable<ContactSummary[]> = combineLatest([
    this.search$,
    this.refresh$
  ]).pipe(
    switchMap(([options]) => {
      const tagResolution$ = options.tagId
        ? this.tagService.getTagFilterTargets(
            [options.tagId],
            TagFilterScope.ClientsOnly
          )
        : of(null as TagFilterTargets | null);
      return tagResolution$.pipe(
        switchMap((tagTargets) => {
          this.isSearching = true;
          const { tagId: _tagId, ...apiOptions } = options;
          return this.contactService.getContacts(apiOptions).pipe(
            map((contacts) => {
              this.isSearching = false;
              if (!options.tagId || !tagTargets) {
                return contacts;
              }
              return contacts.filter((c) =>
                tagTargets.taggedContacts.some(
                  (t) =>
                    t.entityId === c.id &&
                    t.entityType.toLowerCase() ===
                      (c.entityType ?? '').toLowerCase()
                )
              );
            })
          );
        })
      );
    })
  );
  public assignableUsers$: Observable<UserList> =
    this.userService.getAssignableUsers(false, true);  // Don't startWithEmpty to avoid double emission

  modal: BsModalRef | null = null;

  selectedSortOption: SortOption | null = null;

  get contactReassignmentEnabled(): boolean {
    return this.featureFlagService.isEnabled(FeatureFlag.ContactReassignment);
  }

  onSortSelected(sortOption: SortOption | null) {
    this.selectedSortOption = sortOption;
  }

  form = this.fb.group({
    filter: [this.defaultFilterOptions]
  });

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly modalService: BsModalService,
    private readonly contactService: ContactService,
    private readonly userService: UserService,
    private readonly tagService: TagService,
    private registryService: AgpSharedAggregatorComponentRegistryService,
    private readonly featureFlagService: FeatureFlagService
  ) {}

  ngOnInit() {
    this.initializeDefaultFilters();
    this.subscribeToFilterForms();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  public openModal(template: TemplateRef<any>): void {
    this.modal = this.modalService.show(
      template,
      ModalSetting.createLargeModal({})
    );
  }

  public closeModal(): void {
    this.modal?.hide();
    this.modal = null;
  }

  public addContact(entityType: EntityType): void {
    switch (entityType) {
      case EntityType.Individual:
        this.newIndividual();
        break;
      case EntityType.Company:
        this.newCompany();
        break;
      case EntityType.Trust:
        this.newTrust();
        break;
      default:
        break;
    }
  }

  private initializeDefaultFilters() {
    // Initialize form with default filters and set ownedBy to current user if not already set
    this.assignableUsers$.pipe(take(1)).subscribe(users => {
      const currentUser = this.userService.getMyPortfolio(users);
      if (currentUser && !this.defaultFilterOptions.ownedBy) {
        this.defaultFilterOptions.ownedBy = currentUser;
        this.form.setValue({ filter: this.defaultFilterOptions }, { emitEvent: false });
      }
    });
  }

  private subscribeToFilterForms() {
    const subscription = this.form.valueChanges
      .pipe(
        map((v) => v.filter),
        debounceTime(500),
        distinctUntilChanged((x, y) => _.isEqual(x, y))
      )
      .subscribe((v) => {
        this.search$.next(v);
        this.hasFiltersApplied = !_.isEqual(v, this.defaultFilterOptions);
      });
    this.subscriptions.push(subscription);
  }

  private newIndividual(): void {
    this.openFormModal(IndividualNewComponent, {
      individualId: Guid.create().toString(),
      cancel: () => this.closeModal(),
      saved: () => this.closeAndRefresh()
    });
  }

  private newCompany(): void {
    this.openFormModal(CompanyNewComponent, {
      companyId: Guid.create().toString(),
      cancel: () => this.closeModal(),
      saved: () => this.closeAndRefresh()
    });
  }

  private newTrust(): void {
    this.openFormModal(TrustNewComponent, {
      trustId: Guid.create().toString(),
      cancel: () => this.closeModal(),
      saved: () => this.closeAndRefresh()
    });
  }

  private openFormModal(component: any, inputParams: any): void {
    this.closeModal();
    this.modal = this.modalService.show(
      component,
      ModalSetting.createLargeModal(inputParams)
    );
  }

  private closeAndRefresh(): void {
    this.closeModal();
    this.refreshList();
  }

  private refreshList(): void {
    this.refresh$.next(null);
  }

  public openReassign(): void {
    this.closeModal();

    const content: any = {
      cancel: () => this.closeModal(),
      saved: () => this.closeModal()
    };

    this.modal = this.modalService.show(
      ContactReassignComponent,
      ModalSetting.createLargeModal(content)
    );
  }
}
